import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, Input, Checkbox, cn, Button } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import { arrayMove } from "@tellescope/utils";
import { GripVertical, Trash2, Plus } from "lucide-react";
import type { FormResponseListBlock, FormResponseCategory } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import { ItemFilterSection } from "../shared/ItemFilterSection";
import type { BlockTypeEditorProps } from "../types";
import type { VisibilityCondition } from "../../../../../shared/schema/visibility";
import { FORM_RESPONSE_CATEGORY_PREFERRED_ORDER, getFormResponseCategoryInfo } from "../constants";

type FormResponseListBlockEditorProps = BlockTypeEditorProps<FormResponseListBlock>;

interface TitleOverridesSectionProps {
  titleOverrides: Record<string, string> | undefined;
  onChange: (overrides: Record<string, string> | undefined) => void;
}

const TitleOverridesSection = ({ titleOverrides, onChange }: TitleOverridesSectionProps) => {
  const { t } = useTranslation();
  const overrides = titleOverrides ?? {};
  const entries = Object.entries(overrides);

  const addOverride = () => {
    onChange({ ...overrides, "": "" });
  };

  const updateOverrideKey = (oldKey: string, newKey: string) => {
    const newOverrides: Record<string, string> = {};
    for (const [key, value] of Object.entries(overrides)) {
      if (key === oldKey) {
        newOverrides[newKey] = value;
      } else {
        newOverrides[key] = value;
      }
    }
    onChange(Object.keys(newOverrides).length > 0 ? newOverrides : undefined);
  };

  const updateOverrideValue = (key: string, value: string) => {
    onChange({ ...overrides, [key]: value });
  };

  const removeOverride = (keyToRemove: string) => {
    const newOverrides = Object.fromEntries(Object.entries(overrides).filter(([key]) => key !== keyToRemove));
    onChange(Object.keys(newOverrides).length > 0 ? newOverrides : undefined);
  };

  return (
    <div className="space-y-2">
      <Label>{t("Title Overrides (optional)")}</Label>
      <p className="text-xs text-muted-foreground">{t("Rename form titles for display")}</p>

      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map(([originalTitle, displayTitle], index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={originalTitle}
                onChange={(e) => updateOverrideKey(originalTitle, e.target.value)}
                placeholder={t("Original title")}
                className="flex-1"
              />
              <span className="text-muted-foreground">→</span>
              <Input
                value={displayTitle}
                onChange={(e) => updateOverrideValue(originalTitle, e.target.value)}
                placeholder={t("Display title")}
                className="flex-1"
              />
              <Button variant="ghost" size="sm" onClick={() => removeOverride(originalTitle)} className="flex-shrink-0">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button variant="outline" size="sm" onClick={addOverride} className="w-full">
        <Plus className="h-4 w-4 mr-1" />
        {t("Add Title Override")}
      </Button>
    </div>
  );
};

export function FormResponseListBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: FormResponseListBlockEditorProps) {
  const { t } = useTranslation();
  const categories = block.props.categories ?? ["all"];

  // Drag state for category reordering
  const [draggedCategory, setDraggedCategory] = useState<FormResponseCategory | null>(null);

  const toggleCategory = (category: FormResponseCategory, checked: boolean) => {
    let newCategories: FormResponseCategory[];
    if (checked) {
      // Add category - insert in preferred order position relative to existing categories
      const existingIndices = categories.map((c) => FORM_RESPONSE_CATEGORY_PREFERRED_ORDER.indexOf(c));
      const newIndex = FORM_RESPONSE_CATEGORY_PREFERRED_ORDER.indexOf(category);
      // Find where to insert based on preferred order
      let insertAt = categories.length;
      for (let i = 0; i < categories.length; i++) {
        const existingIndex = existingIndices[i] ?? -1;
        if (existingIndex > newIndex) {
          insertAt = i;
          break;
        }
      }
      newCategories = [...categories.slice(0, insertAt), category, ...categories.slice(insertAt)];
    } else {
      // Remove category, but ensure at least one remains
      newCategories = categories.filter((c) => c !== category);
      if (newCategories.length === 0) {
        newCategories = ["all"]; // Fallback to "all" if nothing selected
      }
    }
    updateProps({ categories: newCategories });
  };

  const handleDragStart = (category: FormResponseCategory) => {
    setDraggedCategory(category);
  };

  const handleDragOver = (e: React.DragEvent, targetCategory: FormResponseCategory) => {
    e.preventDefault();
    if (!draggedCategory || draggedCategory === targetCategory) return;

    const fromIndex = categories.indexOf(draggedCategory);
    const toIndex = categories.indexOf(targetCategory);
    if (fromIndex === -1 || toIndex === -1) return;

    const newCategories = arrayMove(categories, fromIndex, toIndex);
    updateProps({ categories: newCategories });
  };

  const handleDragEnd = () => {
    setDraggedCategory(null);
  };

  // Get unselected categories for the "add" section
  const unselectedCategories = FORM_RESPONSE_CATEGORY_PREFERRED_ORDER.filter((c) => !categories.includes(c));

  const updateItemFilter = (condition: VisibilityCondition | undefined) => {
    if (condition) {
      updateProps({ itemFilter: { condition } });
    } else {
      updateProps({ itemFilter: undefined });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Categories")}</Label>
        <p className="text-xs text-muted-foreground">
          {t("Select which form categories to show. Multiple selections will show a filter.")}
        </p>

        {/* Selected categories - draggable for reordering */}
        {categories.length > 0 && (
          <div className="space-y-1 pt-1">
            <p className="text-xs text-muted-foreground font-medium">{t("Selected (drag to reorder)")}</p>
            {categories.map((category) => {
              const info = getFormResponseCategoryInfo()[category];
              const isDragging = draggedCategory === category;
              return (
                <div
                  key={category}
                  draggable={categories.length > 1}
                  onDragStart={() => handleDragStart(category)}
                  onDragOver={(e) => handleDragOver(e, category)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "flex items-center gap-2 p-1.5 rounded border bg-background",
                    categories.length > 1 && "cursor-grab active:cursor-grabbing",
                    isDragging && "opacity-50"
                  )}
                >
                  {categories.length > 1 && <GripVertical className="h-3 w-3 text-muted-foreground flex-shrink-0" />}
                  <Checkbox
                    id={`form-cat-${category}`}
                    checked={true}
                    onCheckedChange={() => toggleCategory(category, false)}
                  />
                  <label htmlFor={`form-cat-${category}`} className="text-sm cursor-pointer flex-1">
                    {info.label}
                    {info.description && (
                      <span className="text-xs text-muted-foreground ml-1">({info.description})</span>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        )}

        {/* Unselected categories - click to add */}
        {unselectedCategories.length > 0 && (
          <div className="space-y-1 pt-2">
            <p className="text-xs text-muted-foreground font-medium">{t("Available")}</p>
            {unselectedCategories.map((category) => {
              const info = getFormResponseCategoryInfo()[category];
              return (
                <div key={category} className="flex items-center gap-2 p-1.5 rounded border border-dashed">
                  <Checkbox
                    id={`form-cat-add-${category}`}
                    checked={false}
                    onCheckedChange={() => toggleCategory(category, true)}
                  />
                  <label
                    htmlFor={`form-cat-add-${category}`}
                    className="text-sm cursor-pointer text-muted-foreground flex-1"
                  >
                    {info.label}
                    {info.description && (
                      <span className="text-xs text-muted-foreground ml-1">({info.description})</span>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label>{t("Max Items to Show")}</Label>
        <Select
          value={String(block.props.maxItems ?? 5)}
          onValueChange={(value) => updateProps({ maxItems: parseInt(value, 10) })}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="1">1</Select.Item>
            <Select.Item value="2">2</Select.Item>
            <Select.Item value="3">3</Select.Item>
            <Select.Item value="5">5</Select.Item>
            <Select.Item value="10">10</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("Empty Message (optional)")}</Label>
        <Input
          value={block.props.emptyMessage || ""}
          onChange={(e) => updateProps({ emptyMessage: e.target.value || undefined })}
          placeholder={t("No forms")}
        />
      </div>

      <TitleOverridesSection
        titleOverrides={block.props.titleOverrides}
        onChange={(overrides) => updateProps({ titleOverrides: overrides })}
      />

      <ItemFilterSection
        itemFilter={block.props.itemFilter}
        onChange={updateItemFilter}
        description={t(
          "Filter which form responses are displayed based on conditions. Use 'Form Responses' source for form response properties."
        )}
      />

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
